import { useQuery } from 'convex/react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Loader2, Building2, User } from 'lucide-react'
import { api } from '../../../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'

function ExternalLinkPill({ href, label }: { href?: string; label: string }) {
  if (!href) return null
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-neutral-300 bg-white text-neutral-700 hover:border-neutral-500 hover:text-neutral-900 transition-colors"
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  )
}

function ChipList({ items }: { items: string[] }) {
  if (!items.length) return <span className="text-xs text-neutral-400">—</span>
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((s) => (
        <span key={s} className="px-2 py-0.5 text-xs rounded-full border bg-neutral-100 text-neutral-700 border-neutral-200">
          {s}
        </span>
      ))}
    </div>
  )
}

function Stat({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`rounded-lg border border-neutral-200 bg-white p-3 ${wide ? 'sm:col-span-2' : ''}`}>
      <div className="text-[11px] uppercase tracking-wide text-neutral-500 mb-1">{label}</div>
      {children}
    </div>
  )
}

export default function AdminFundDetailPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const { slug } = useParams<{ slug: string }>()

  const data = useQuery(api.vcdb.getFund, slug ? { slug } : 'skip')

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }
  if (!data) {
    return (
      <div className="p-6 flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }
  if (data === null) {
    return (
      <div className="p-6 space-y-2">
        <Link to="/admin/investors" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700">
          <ArrowLeft className="h-4 w-4" /> All investors
        </Link>
        <div className="text-sm text-neutral-500">Fund not found.</div>
      </div>
    )
  }

  const { fund, partners } = data

  return (
    <div className="p-6 max-w-4xl space-y-6">
      <Link to="/admin/investors" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700">
        <ArrowLeft className="h-4 w-4" /> All investors
      </Link>

      <div className="flex items-start gap-4">
        {fund.logoUrl ? (
          <img
            src={fund.logoUrl}
            alt={fund.name}
            className="h-20 w-20 rounded object-contain bg-white border border-neutral-200 p-1.5 shrink-0"
          />
        ) : (
          <div className="h-20 w-20 rounded bg-neutral-100 flex items-center justify-center shrink-0">
            <Building2 className="h-9 w-9 text-neutral-400" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold text-neutral-900">{fund.name.trim()}</h1>
          {fund.location && <div className="text-sm text-neutral-600 mt-0.5">{fund.location}</div>}
          {fund.fundSizeText && <div className="text-xs text-neutral-500 mt-0.5">{fund.fundSizeText}</div>}
          <div className="flex flex-wrap gap-2 mt-3">
            <ExternalLinkPill href={fund.websiteUrl} label="Website" />
            <ExternalLinkPill href={fund.sourceUrl} label="vcsheet" />
          </div>
        </div>
      </div>

      {fund.description && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">About</h2>
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{fund.description}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Stat label="Stages">
          <ChipList items={fund.stages} />
        </Stat>
        <Stat label="Geo">
          <ChipList items={fund.geo} />
        </Stat>
        <Stat label="Sectors" wide>
          <ChipList items={fund.sectors} />
        </Stat>
      </div>

      {fund.thesis && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">What makes them unique</h2>
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{fund.thesis}</p>
        </div>
      )}

      {fund.portfolio && fund.portfolio.length > 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Notable investments</h2>
          <div className="flex flex-wrap gap-1.5">
            {fund.portfolio.map((p) => (
              <a
                key={(p.slug ?? p.name) + (p.sector ?? '')}
                href={p.slug ? `https://www.vcsheet.com/company/${p.slug}` : undefined}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border bg-neutral-100 text-neutral-700 border-neutral-200 hover:border-neutral-400"
              >
                <span className="font-medium">{p.name}</span>
                {p.sector && <span className="text-neutral-500">· {p.sector}</span>}
              </a>
            ))}
          </div>
        </div>
      )}

      {fund.industries && fund.industries.length > 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Industries</h2>
          <ChipList items={fund.industries} />
        </div>
      )}

      {fund.sheetSlugs && fund.sheetSlugs.length > 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Listed in</h2>
          <div className="flex flex-wrap gap-1.5">
            {fund.sheetSlugs.map((s) => (
              <a
                key={s}
                href={`https://www.vcsheet.com/sheet/${s}`}
                target="_blank"
                rel="noreferrer noopener"
                className="px-2 py-0.5 text-xs rounded-full border bg-neutral-100 text-neutral-700 border-neutral-200 hover:border-neutral-400"
              >
                {s.replace(/-/g, ' ')}
              </a>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
          {partners.length} partner{partners.length === 1 ? '' : 's'} in our database
        </h2>
        {partners.length === 0 ? (
          <div className="text-sm text-neutral-400">No scraped partners for this fund yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {partners.map((p) => (
              <Link
                key={p.slug}
                to={`/admin/investors/${p.slug}`}
                className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-white p-3 hover:border-neutral-400 transition-colors"
              >
                {p.headshotUrl ? (
                  <img src={p.headshotUrl} alt={p.name} className="h-10 w-10 rounded-full object-cover bg-neutral-100" loading="lazy" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center">
                    <User className="h-4 w-4 text-neutral-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-neutral-900 truncate">{p.name}</div>
                  <div className="text-xs text-neutral-500 truncate">{p.role ?? p.title ?? '—'}</div>
                  {p.avgCheckText && <div className="text-xs text-neutral-600 mt-0.5">{p.avgCheckText}</div>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
